import React, { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle, Layers, Check, User, MapPin, FileSignature, Upload, MessageSquare, Sparkles } from 'lucide-react'
import { SaleWizard } from './SaleWizard'
import { HubShellHeader, StepTabs, type HubStep } from './HubShell'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { SalesCompletionNextStep } from './SalesCompletionNextStep'
import { daysUntilDeadline } from '../../lib/identification/countdown'

type TabKey = 'initial' | 'exchange' | 'acquisition'
type Phase1Step = 'exchange-info' | 'upload-documents' | 'exchangers' | 'property-details' | 'signatures'

export const ExchangeWizardHub: React.FC<{ onClose: () => void; onSectionComplete?: (section: TabKey) => void; initialTab?: TabKey }> = ({ onClose, onSectionComplete, initialTab: _initialTab }) => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const [phase1Step, setPhase1Step] = useState<Phase1Step>('exchange-info')
  const [completionModalOpen, setCompletionModalOpen] = useState(false)
  const [phase1StepsCompleted, setPhase1StepsCompleted] = useState<Record<Phase1Step, boolean>>({ 'exchange-info': false, 'upload-documents': false, exchangers: false, 'property-details': false, signatures: false })
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [ticketMessage, setTicketMessage] = useState('')
  const [isSalesHubSubmitted, setIsSalesHubSubmitted] = useState(() => localStorage.getItem('sales_hub_submitted') === 'true')
  const [_isInEscrow, setIsInEscrow] = useState(false)
  const [_hasPreliminaryTitleReport, setHasPreliminaryTitleReport] = useState(false)
  const controlsRef = useRef<any | null>(null)
  const identificationAccess = useHubAccess('identification', user)
  const salesCompletionPercent = useMemo(() => {
    if (isSalesHubSubmitted) return 100
    const total = Object.keys(phase1StepsCompleted).length
    const completed = Object.values(phase1StepsCompleted).filter(Boolean).length
    return total > 0 ? Math.round((completed / total) * 100) : 0
  }, [phase1StepsCompleted, isSalesHubSubmitted])

  const idDaysRemaining = useMemo(() => {
    if (currentExchange?.identificationDeadline) {
      return Math.max(0, daysUntilDeadline(currentExchange.identificationDeadline))
    }
    return 45
  }, [currentExchange?.identificationDeadline])
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const savedStep = localStorage.getItem(`sales_hub_phase1_step_${user.id}`) as Phase1Step | null
    if (savedStep) {
      setPhase1Step(savedStep)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    localStorage.setItem(`sales_hub_phase1_step_${user.id}`, phase1Step)
  }, [user?.id, phase1Step])
  
  // Check if Sales Hub can be submitted (all steps complete including signatures)
  const canSubmitSalesHub = useMemo(() => {
    if (isSalesHubSubmitted) return false
    const allStepsComplete = Object.values(phase1StepsCompleted).every(v => v === true)
    return allStepsComplete
  }, [phase1StepsCompleted, isSalesHubSubmitted])

  // Update step completion status based on form state
  useEffect(() => {
    const updateCompletion = () => {
      if (!controlsRef.current?.getPhase1StepCompletion) return
      const completion = controlsRef.current.getPhase1StepCompletion()
      if (completion) {
        setPhase1StepsCompleted(completion)
      }
      
      // Update escrow state
      if (controlsRef.current?.getEscrowState) {
        const escrowState = controlsRef.current.getEscrowState()
        if (escrowState) {
          setIsInEscrow(escrowState.isInEscrow)
          setHasPreliminaryTitleReport(escrowState.hasPreliminaryTitleReport)
        }
      }
    }
    
    // Check immediately
    const timeout = setTimeout(updateCompletion, 100)
    
    // Check periodically to update as user fills in fields
    const interval = setInterval(updateCompletion, 1000)
    
    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [phase1Step]) // Re-check when step changes

  useEffect(() => {
    const handleSalesHubCompleted = () => {
      setIsSalesHubSubmitted(true)
      setPhase1StepsCompleted(prev => ({ ...prev }))
      setCompletionModalOpen(true)
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    }
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted)
    return () => window.removeEventListener('salesHubCompleted', handleSalesHubCompleted)
  }, [])

  useEffect(() => {
    const handleAutoSaveUpdate = () => {
      const savedTime = controlsRef.current?.getLastSavedAt?.()
      if (savedTime) {
        setLastSavedAt(savedTime)
      }
    }
    window.addEventListener('wizardDataUpdated', handleAutoSaveUpdate)
    handleAutoSaveUpdate()
    return () => window.removeEventListener('wizardDataUpdated', handleAutoSaveUpdate)
  }, [])
  // Also check localStorage periodically to catch state changes
  useEffect(() => {
    const checkSubmission = () => {
      const submitted = localStorage.getItem('sales_hub_submitted') === 'true'
      if (submitted !== isSalesHubSubmitted) {
        setIsSalesHubSubmitted(submitted)
      }
    }
    
    // Check immediately
    checkSubmission()
    
    // Check periodically
    const interval = setInterval(checkSubmission, 500)
    return () => clearInterval(interval)
  }, [isSalesHubSubmitted])

  const handleRequestEdit = () => {
    if (!isSalesHubSubmitted) {
      toast.error('Please submit the Sales Hub section before requesting edits')
      return
    }
    setShowTicketModal(true)
  }

  const handleSubmitTicket = () => {
    if (!ticketMessage.trim()) {
      toast.error('Please enter a description of the changes needed')
      return
    }
    
    // Mock ticket submission
    const ticketNumber = `TICKET-${Date.now().toString().slice(-6)}`
    
    toast.success(`Edit request submitted! Ticket #${ticketNumber}`)
    
    // Store ticket in localStorage (mock)
    const tickets = JSON.parse(localStorage.getItem('edit_tickets') || '[]')
    tickets.push({
      ticketNumber,
      message: ticketMessage,
      status: 'pending',
      createdAt: new Date().toISOString(),
      section: 'Sales Hub'
    })
    localStorage.setItem('edit_tickets', JSON.stringify(tickets))
    
    // Reset and close
    setTicketMessage('')
    setShowTicketModal(false)
  }

  const handlePhase1StepComplete = (step: Phase1Step) => {
    setPhase1StepsCompleted(prev => ({ ...prev, [step]: true }))
    const allComplete = Object.values({ ...phase1StepsCompleted, [step]: true }).every(v => v)
    if (allComplete) {
      onSectionComplete?.('initial')
    }
  }

  const handleGoToIdHub = () => {
    setCompletionModalOpen(false)
    onClose()
    window.dispatchEvent(new CustomEvent('openIdentificationHub'))
  }

  const handleGoToEscrowHub = () => {
    setCompletionModalOpen(false)
    onClose()
    window.dispatchEvent(new CustomEvent('openEscrowHub'))
  }

  const salesHubSteps: HubStep[] = [
    { key: 'exchange-info', label: 'Exchange Information', icon: Sparkles, completed: phase1StepsCompleted['exchange-info'] },
    { key: 'upload-documents', label: 'Upload Documents', icon: Upload, completed: phase1StepsCompleted['upload-documents'] },
    { key: 'exchangers', label: 'Exchanger Information', icon: User, completed: phase1StepsCompleted['exchangers'] },
    { key: 'property-details', label: 'Property & Buyer Details', icon: MapPin, completed: phase1StepsCompleted['property-details'] },
    { key: 'signatures', label: 'E-Sign Documents', icon: FileSignature, completed: phase1StepsCompleted['signatures'] },
  ]

  const hubContent = createPortal(
    <div className="modal-overlay modal-overlay-sales-hub">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-full overflow-hidden flex flex-col">
        <HubShellHeader
          icon={Layers}
          title="Sales Hub"
          subtitle="Upload → extract → confirm → e-sign."
          completionPercent={salesCompletionPercent}
          isSubmitted={isSalesHubSubmitted}
          color="green"
          onClose={onClose}
        />

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-slate-800 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <StepTabs
              steps={salesHubSteps}
              activeStep={phase1Step}
              onStepChange={(key) => setPhase1Step(key as Phase1Step)}
              sectionTitle="Quick Steps"
              color="green"
              isSubmitted={isSalesHubSubmitted}
            />
            <SaleWizard
              embedded
              hideFooter
              exposeControls={(api) => (controlsRef.current = api)}
              mode="phase1"
              phase1Step={phase1Step}
              onClose={onClose}
              onComplete={() => handlePhase1StepComplete(phase1Step)}
              onPhase1StepChange={setPhase1Step}
            />
          </div>
        </div>

        {/* Footer - global navigation */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 md:px-6 py-3 md:py-4 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between gap-2">
            {isSalesHubSubmitted ? (
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300 truncate">Submitted successfully</p>
                  <p className="text-xs text-green-600 dark:text-green-400 hidden md:block">
                    {identificationAccess.canAccess
                      ? 'Identification Hub is now unlocked'
                      : 'Please verify your identity to unlock the next phase'}
                  </p>
                </div>
              </div>
            ) : (
              <Button 
                onClick={handleRequestEdit}
                disabled={true}
                variant="outline"
                className="border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500 cursor-not-allowed opacity-50"
                title="Request Edit"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Request Edit
              </Button>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isSalesHubSubmitted && (
                <div className="flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-1 text-xs text-slate-600 dark:text-slate-300">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span>
                    {lastSavedAt ? `Auto-saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Auto-save on'}
                  </span>
                </div>
              )}
              <Button 
                className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                onClick={() => {
                  try {
                    if (controlsRef.current?.submit) {
                      controlsRef.current.submit()
                    } else {
                      toast.error('Wizard not ready. Please wait a moment and try again.')
                    }
                  } catch {
                    toast.error('Failed to submit. Please try again.')
                  }
                }}
                disabled={isSalesHubSubmitted || !canSubmitSalesHub}
                title={!canSubmitSalesHub && !isSalesHubSubmitted ? 'Please complete all steps and sign both documents before submitting' : (isSalesHubSubmitted ? 'Submitted' : 'Submit')}
              >
                <Layers className="h-4 w-4 mr-1" />
                Submit Hub
              </Button>
              {isSalesHubSubmitted && (
                <Button 
                  onClick={handleRequestEdit}
                  variant="outline"
                  className="group relative border-2 border-green-600/30 text-green-600 hover:border-green-600 hover:bg-green-600/10 dark:border-green-400/40 dark:text-green-400 dark:hover:border-green-400 dark:hover:bg-green-400/20 hover:shadow-md transition-all duration-200 px-2 md:px-3"
                  title="Request Edit"
                >
                  <MessageSquare className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Request Edit</span>
                </Button>
              )}
              <Button 
                onClick={onClose} 
                variant="outline"
                className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                title="Close"
              >
                <X className="h-4 w-4 mr-1" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Request Ticket Modal */}
      {showTicketModal && (
        <div className="modal-overlay modal-overlay-nested">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-600/10 to-green-500/10 dark:from-green-600/20 dark:to-green-500/20">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-600/20 dark:bg-green-600/30 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Request Edit</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Sales Hub Section</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTicketModal(false)} 
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  What changes do you need?
                </label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe the edits or corrections needed for your Sales Hub information..."
                  className="w-full px-4 py-3 border-2 border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-green-600 focus:border-green-600 dark:focus:ring-green-400 dark:focus:border-green-400 transition-all resize-none"
                  rows={6}
                  autoFocus
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">ℹ️</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      What happens next?
                    </h4>
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      Our support team will review your request and contact you within 1-2 business days. You'll receive a confirmation email with your ticket number.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <Button
                onClick={() => setShowTicketModal(false)}
                variant="outline"
                className="border-slate-300 dark:border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                disabled={!ticketMessage.trim()}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 dark:from-green-600 dark:to-green-500 dark:hover:from-green-700 dark:hover:to-green-600 text-white border-0 shadow-lg"
              >
                <Check className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  )

  return (
    <>
      {hubContent}
      <SalesCompletionNextStep
        open={completionModalOpen}
        idDaysRemaining={idDaysRemaining}
        onGoToIdHub={handleGoToIdHub}
        onGoToEscrowHub={handleGoToEscrowHub}
        onClose={() => setCompletionModalOpen(false)}
      />
    </>
  )
}


